import { GameEvent } from 'game/events/gameEvent';
import { EventName } from 'game/events/eventsName';

import { GAEvent, GAEventNames } from 'game/analytics/events';
import TimeCounter from './timeCounter';
import { GameStore, Store } from 'game/store/gameStore';
import { translate } from 'game/translate';
import MoveCounter from './moveCounter';
import { SoundManager } from 'game/core/soundManager';
import { GameName } from 'constants/difficulty';
import { isIOS } from 'game/core/display';
import { showShareModal } from 'game/shareModal';
import { preloadDesignAssets } from 'game/preloadGameAssets';
import { MagicWandButton } from './MagicWandButton';
import { openFeedbackPopup } from 'game/feedback';
import { UIState, uiStore, updateUIState } from 'ui';
import { ActionType } from 'game/quests/types';
import { StoriesHandler } from './UiHandlers/StoriesHandler';
import { NewGameHandler } from './UiHandlers/NewGameHandler';
import { Popups, PopupStateData } from 'ui/types/popups';
import { closePopup, openPopup } from 'ui/store/actions/popupsActions';

addEventListener('showInGamePopup', (event: Event) => {
    GameUI.showPopup((event as CustomEvent).detail);
});

addEventListener('closeInGamePopup', (event: Event) => {
    GameUI.closePopup((event as CustomEvent).detail);
});

export type DesignType = 'card_back' | 'card_front' | 'background';

export class GameUI {
    timeCounter: TimeCounter;
    moveCounter: MoveCounter;
    magicWandButton: MagicWandButton;
    storiesHandler: StoriesHandler;
    newGameHandler: NewGameHandler;
    private buttonRestoreTimeoutId?: number;
    private uiBlockedUntil: number = 0;

    options: { key: string, action: () => void, visible: GameName[], valueGetter: (store: GameStore) => boolean }[] = [
        {
            key: 'sounds',
            visible: [GameName.KLONDIKE, GameName.SPIDER, GameName.FREECELL, GameName.PYRAMID],
            action: this.soundsToggle.bind(this),
            valueGetter: (store: GameStore) => store.soundOn,
        },
        {
            key: 'autocomplete',
            visible: [GameName.KLONDIKE, GameName.SPIDER, GameName.FREECELL],
            action: this.autocompleteToggle.bind(this),
            valueGetter: (store: GameStore) => store.autocomplete,
        },
        {
            key: 'leftHanded',
            visible: [GameName.KLONDIKE, GameName.SPIDER, GameName.FREECELL, GameName.PYRAMID],
            action: this.leftHandedToggle.bind(this),
            valueGetter: (store: GameStore) => store.leftHanded,
        },
        {
            key: 'cardHighlight',
            visible: [GameName.SPIDER, GameName.FREECELL, GameName.PYRAMID],
            action: this.cardHighlightToggle.bind(this),
            valueGetter: (store: GameStore) => store.cardHighlighting,
        },
        {
            key: 'autoPlay',
            visible: [GameName.KLONDIKE, GameName.FREECELL],
            action: this.autoPlayToggle.bind(this),
            valueGetter: (store: GameStore) => store.autoPlay,
        },
    ]

    constructor() {
        this.timeCounter = new TimeCounter();
        this.moveCounter = new MoveCounter();
        this.magicWandButton = new MagicWandButton(0);
        this.storiesHandler = new StoriesHandler();
        this.newGameHandler = new NewGameHandler();
        
        document.addEventListener('ui-button-click', this.handleUIButtonClick.bind(this));
        document.addEventListener('ui-setting-change', this.handleUISettingChange.bind(this));
        document.addEventListener('ui-design-change', this.handleUIDesignChange.bind(this));
        document.addEventListener('ui-modal-action', this.handleModalAction.bind(this));
        document.addEventListener('game-ui-show-granted-ticket', this.handleOpenGrantedTicket.bind(this));
        
        GameEvent.add(EventName.ADS_GRANTED, this.UpdateWandCounter.bind(this));
        GameEvent.add(EventName.WAND_SHOW, this.UpdateWandCounter.bind(this));

        window.addEventListener('game-ui-setting-changed', this.onSettingChanged.bind(this));

        GameEvent.add(EventName.CTR_Z, this.OnUndoClicked.bind(this));
        GameEvent.add(EventName.SET_UNDO_DISABLED, this.setUndoDisabled.bind(this));
        GameEvent.add(EventName.SET_HINT_DISABLED, this.setHintDisabled.bind(this));
        GameEvent.add(EventName.SET_WAND_DISABLED, this.setWandDisabled.bind(this));
        GameEvent.add(EventName.SET_STORIES_DISABLED, this.setStoriesDisabled.bind(this));
        GameEvent.add(EventName.ADS_GRANTED, this.updateButtons.bind(this));
        GameEvent.add(EventName.UPDATE_HINT_BUTTON, this.updateHintsCounter.bind(this));
        GameEvent.add(EventName.BLOCK_WAND_AND_HINTS, this.onBlockActionButtons.bind(this));
        // Block all UI interactions during any game-driven block
        GameEvent.add(EventName.BLOCK_ACTION_BUTTONS, this.onBlockAllButtons.bind(this));
        // Explicitly block during autocomplete flow
        GameEvent.add(EventName.RUN_AUTOCOMPLETE, this.onBlockAllButtons.bind(this, Infinity));
        GameEvent.add(EventName.AUTOCOMPLETE_COMPLETE, this.onBlockAllButtons.bind(this, 0));
        // Block briefly on each autoplay move trigger
        GameEvent.add(EventName.AUTO_PLAY, this.onBlockAllButtons.bind(this, 800));
        GameEvent.add(EventName.START_TUTORIAL, () => {
            this.updateUI({ tutorialActive: true });
        });
        GameEvent.add(EventName.STOP_TUTORIAL, () => {
            this.updateUI({ tutorialActive: false });
        });

        GameEvent.add(EventName.UI_UPDATE_COUNTERS, this.updateButtons.bind(this));
    }

    private handleOpenGrantedTicket(e: Event) {
        this.updateUI({
            modals: {
                ...uiStore.getState().gameUI.modals,
                storiesModal: { ...uiStore.getState().gameUI.modals.storiesModal, grantedTickets: (e as CustomEvent)?.detail }
            }
        });
    }

    updateUI(updates: Partial<UIState>) {
        uiStore.dispatch(updateUIState(updates));
    }

    private handleUIButtonClick(event: Event) {
        if (this.isUIBlocked()) return;
        const customEvent = event as CustomEvent;
        const { buttonKey } = customEvent.detail;
        
        switch (buttonKey) {
            case 'settings':
                this.onOpenSettings();
                break;
            case 'design':
            case 'whats-new-btn':
                this.onOpenDesign();
                break;
            case 'share':
                this.onShare();
                break;
            case 'tutorial':
                this.rulesClick();
                break;
            case 'feedback':
                this.onOpenFeedback();
                break;
            case 'magic-wand':
                this.magicWandButton.onClick();
                break;
            case 'hints':
                this.onHintClicked();
                break;
            case 'undo':
                this.OnUndoClicked();
                break;
            case 'close-settings':
                this.onHideSettings();
                break;
            case 'close-design':
                this.onHideDesign();
                break;
            case 'mission-ad':
                this.onMissionAdWatch();
                break;
            case 'missions':
                this.onOpenMissions();
                break;
            case 'close-missions':
                this.onHideMissions();
                break;
            case 'mission-claim-rewards-daily':
                this.onSideBarClaimDailyReward();
                break;
            case 'mission-claim-rewards-weekly':
                this.onClaimRewards('weekly');
                break;
            case 'new-missions-request':
                this.onNewMissionsRequest();
                break;
            case 'win-popup-missions-claim-reward-btn':
                this.OnClaimWinPopupButton();
                break;
            case 'win-popup-missions':
                this.onWinPopupMissionsClick();
                break;
            case 'win-popup-mission-continue-btn':
                this.handleStartGameAfterMissionViaWinPopup();
                break;
        }
    }

    private onSideBarClaimDailyReward() {
        GAEvent(GAEventNames.MISSION_REWARD_CLAIMED, {
            placement: 'sidebar',
        });
        this.onClaimRewards('daily');
    }

    private handleStartGameAfterMissionViaWinPopup() {
        dispatchEvent(new CustomEvent('newWinGame'));
    }
    
    private OnClaimWinPopupButton() {
        GAEvent(GAEventNames.MISSION_REWARD_CLAIMED, {
            placement: 'winscreen',
        });

        SoundManager.play('missionClaimRewardsSFX');
        
        this.onClaimRewards('daily');

        this.updateUI({
            modals: {
                ...uiStore.getState().gameUI.modals,
                missionCompletePopup: {
                    placement: 'winscreen',
                    isOpen: true
                }
            },
            missions: {
                ...uiStore.getState().gameUI.missions,
                isNewMissionCompletedOnMounted: false,
                isNewMissionCompleted: false,
            },
            isExpanded: true
        });
    }

    private onWinPopupMissionsClick() {
        GAEvent(GAEventNames.MISSION_CLICK_ON_WIN_POPUP);
        this.updateUI({
            missions: {
                ...uiStore.getState().gameUI.missions,
                isNewMissionCompletedOnMounted: false,
                isNewMissionCompleted: false,
            },
        });
        this.onOpenMissions();
        dispatchEvent(new CustomEvent('newWinGame'));
    }

    private onNewMissionsRequest() {
        GameEvent.emit(EventName.NEW_MISSIONS_REQUEST);
    }

    private onClaimRewards(type: 'daily' | 'weekly') {
        GameEvent.emit(EventName.MISSION_CALIM_REWARDS, type);
    }

    private isUIBlocked(): boolean {
        return this.uiBlockedUntil === Infinity || performance.now() < this.uiBlockedUntil;
    }

    private onBlockAllButtons(duration: number) {
        if (duration === 0) {
            this.uiBlockedUntil = 0;
            this.onBlockActionButtons(duration);
            return;
        }
        this.uiBlockedUntil = duration === Infinity ? Infinity : performance.now() + duration;
        this.onBlockActionButtons(duration);
    }

    private onMissionAdWatch() {
        GameEvent.emit(EventName.SHOW_ADS, { place: 'mission' });
    }

    private handleUISettingChange(event: Event) {
        const customEvent = event as CustomEvent;
        const { settingKey } = customEvent.detail;
        
        const option = this.options.find(opt => opt.key === settingKey);
        if (option) {
            option.action();
            
            const updatedSettings = this.options.map(opt => ({
                checked: opt.valueGetter(Store),
                visible: opt.visible.includes(Store.name),
                key: opt.key,
                label: translate(opt.key)
            }));
            
            this.updateUI({
                settings: updatedSettings
            });
        } else {
            console.error('GameUI: Option not found for key:', settingKey);
        }
    }

    private handleModalAction(event: Event) {
        const customEvent = event as CustomEvent;
        const { action, tab } = customEvent.detail;
        
        switch (action) {
            case 'change-tab':
                this.updateUI({
                    modals: {
                        ...uiStore.getState().gameUI.modals,
                        designModal: {
                            ...uiStore.getState().gameUI.modals.designModal,
                            activeTab: tab
                        }
                    }
                });
                break;
            default:
                console.warn('Unknown modal action:', action);
        }
    }

    private handleUIDesignChange(event: Event) {
        const customEvent = event as CustomEvent;
        const { type, value } = customEvent.detail;
        
        switch (type) {
            case 'card_back':
                Store.setBack(value);
                preloadDesignAssets(type, EventName.CHANGE_CARD_BACK);
                this.updateUI({
                    design: {
                        ...uiStore.getState().gameUI.design,
                        selectedPattern: value
                    }
                });
                GAEvent(GAEventNames.GAME_DESIGN_BACK, { number: value });

                GameEvent.emit(EventName.MISSION_ACTION, { type: ActionType.DESIGN_CHANGE_CARD_BACK, payload: { type }, currentProgress: 1 });
                break;
            case 'card_front':
                Store.setFront(value);
                preloadDesignAssets(type, EventName.CHANGE_CARD_FRONT);
                this.updateUI({
                    design: {
                        ...uiStore.getState().gameUI.design,
                        selectedCardTheme: value
                    }
                });
                GAEvent(GAEventNames.GAME_DESIGN_FRONT, { number: value });
                
                GameEvent.emit(EventName.MISSION_ACTION, { type: ActionType.DESIGN_CHANGE_CARD_FRONT, payload: { type },currentProgress: 1 });
                break;
            case 'background':
                Store.setBackground(value);
                GameEvent.emit(EventName.CHANGE_BG);
                this.updateUI({
                    design: {
                        ...uiStore.getState().gameUI.design,
                        selectedBackground: value
                    }
                });
                GAEvent(GAEventNames.GAME_DESIGN_BACKGROUND, { number: value });

                GameEvent.emit(EventName.MISSION_ACTION, { type: ActionType.DESIGN_CHANGE_BACKGROUND, payload: { type }, currentProgress: 1 });
                break;
        }
    }

    private onShare() {
        showShareModal();
    }

    private onOpenFeedback() {
        GameEvent.emit(EventName.HIDE_SETTINGS);

        this.updateUI({
            modals: {
                ...uiStore.getState().gameUI.modals,
                settingsModal: { ...uiStore.getState().gameUI.modals.settingsModal, isOpen: false },
                designModal: { ...uiStore.getState().gameUI.modals.designModal, isOpen: false },
                storiesModal: { ...uiStore.getState().gameUI.modals.storiesModal, isOpen: false },
                missionsModal: { ...uiStore.getState().gameUI.modals.missionsModal, isOpen: false },
                newGameModal: { ...uiStore.getState().gameUI.modals.newGameModal, isOpen: false },
            },
            isExpanded: false
        });
        openFeedbackPopup('button');
    }

    private updateButtons() {
        this.updateHintsCounter();
        this.UpdateWandCounter();
    }

    private onSettingChanged(e: any) {
        const { key } = e.detail;

        const setting = this.options.find(option => option.key === key);

        setting?.action();
    }

    soundsToggle() {
        GameEvent.emit(EventName.TOGGLE_SOUND);
        GAEvent(GAEventNames.GAME_SETTINGS_SOUNDS, {
            status: Store.soundOn ? 'ON' : 'OFF',
        });
    }

    autocompleteToggle() {
        GameEvent.emit(EventName.TOGGLE_AUTOCOMPLETE);
        GAEvent(GAEventNames.GAME_SETTINGS_AUTOCOMPLETE, {
            status: Store.autocomplete ? 'ON' : 'OFF',
        });
    }

    leftHandedToggle() {
        GameEvent.emit(EventName.TOGGLE_LEFT_HANDED);
        GAEvent(GAEventNames.GAME_SETTINGS_HAND, {
            hand: Store.leftHanded ? 'Left' : 'Right',
        });
    }
    cardHighlightToggle() {
        GameEvent.emit(EventName.TOGGLE_CARD_HIGHLIGHTING);
        GAEvent(GAEventNames.GAME_SETTINGS_HIGHLIGHT, {
            status: Store.cardHighlighting ? 'ON' : 'OFF',
        });
    }
    autoPlayToggle() {
        GameEvent.emit(EventName.TOGGLE_AUTOPLAY);
        GAEvent(GAEventNames.GAME_SETTINGS_AUTOPLAY, {
            status: Store.autoPlay ? 'ON' : 'OFF',
        });
    }

    private onOpenSettings() {
        GAEvent(GAEventNames.GAME_SETTINGS_OPEN, {});
        GameEvent.emit(EventName.OPEN_SETTINGS);
        
        this.updateUI({
            modals: {
                ...uiStore.getState().gameUI.modals,
                newGameModal: { isOpen: false },
                settingsModal: { isOpen: true },
                designModal: { ...uiStore.getState().gameUI.modals.designModal, isOpen: false },
                storiesModal: { ...uiStore.getState().gameUI.modals.storiesModal, isOpen: false },
                missionsModal: { ...uiStore.getState().gameUI.modals.missionsModal, isOpen: false },
            },
            isExpanded: true
        });
    }

    private onHideSettings() {
        GameEvent.emit(EventName.HIDE_SETTINGS);

        this.updateUI({
            modals: {
                ...uiStore.getState().gameUI.modals,
                settingsModal: { ...uiStore.getState().gameUI.modals.settingsModal, isOpen: false }
            },
            isExpanded: false
        });
    }

    private onHideDesign() {
        GameEvent.emit(EventName.HIDE_SETTINGS);

        this.updateUI({
            modals: {
                ...uiStore.getState().gameUI.modals,
                designModal: { ...uiStore.getState().gameUI.modals.designModal, isOpen: false }
            },
            isExpanded: false
        });
    }

    private onOpenDesign() {
        GAEvent(GAEventNames.GAME_DESIGN_OPEN, {});
        GameEvent.emit(EventName.OPEN_SETTINGS);
        
        const activeTab = uiStore.getState().gameUI.buttons.hideDesignFirstTab ? 'cards' : 'patterns';

        this.updateUI({
            modals: {
                ...uiStore.getState().gameUI.modals,
                newGameModal: { isOpen: false },
                storiesModal: { ...uiStore.getState().gameUI.modals.storiesModal, isOpen: false },
                missionsModal: { ...uiStore.getState().gameUI.modals.missionsModal, isOpen: false },
                designModal: { ...uiStore.getState().gameUI.modals.designModal, isOpen: true, activeTab },
                settingsModal: { isOpen: false },
            },
            isExpanded: true
        });
    }

    private onOpenMissions() {
        GameEvent.emit(EventName.OPEN_SETTINGS);

        this.updateUI({
            modals: {
                ...uiStore.getState().gameUI.modals,
                newGameModal: { isOpen: false },
                storiesModal: { ...uiStore.getState().gameUI.modals.storiesModal, isOpen: false },
                designModal: { ...uiStore.getState().gameUI.modals.designModal, isOpen: false },
                settingsModal: { isOpen: false },
                missionsModal: { isOpen: true, weeklyDisabled: true }
            },
            isExpanded: true
        });
    }

    private onHideMissions() {
        GameEvent.emit(EventName.HIDE_SETTINGS);

        this.updateUI({
            modals: {
                ...uiStore.getState().gameUI.modals,
                missionsModal: { ...uiStore.getState().gameUI.modals.missionsModal, isOpen: false }
            },
            isExpanded: false
        });
    }

    public setUndoDisabled(disabled: boolean) {
        this.updateUI({
            buttons: {
                ...uiStore.getState().gameUI.buttons,
                undoDisabled: disabled
            }
        });
    }

    public setHintDisabled(disabled: boolean) {
        this.updateUI({
            buttons: {
                ...uiStore.getState().gameUI.buttons,
                hintsDisabled: disabled
            }
        });
    }

    public setWandDisabled(disabled: boolean) {
        this.updateUI({
            buttons: {
                ...uiStore.getState().gameUI.buttons,
                magicWandDisabled: disabled
            }
        });
    }

    public setStoriesDisabled(disabled: boolean) {
        this.updateUI({
            buttons: {
                ...uiStore.getState().gameUI.buttons,
                storiesDisabled: disabled
            }
        });
    }

    private onBlockActionButtons(duration: number) {
        
        if (this.buttonRestoreTimeoutId) {
            clearTimeout(this.buttonRestoreTimeoutId);
            this.buttonRestoreTimeoutId = undefined;
        }
        
        this.setUndoDisabled(true);
        this.setHintDisabled(true);
        this.setWandDisabled(true);
        this.setStoriesDisabled(true);
        
        if (duration != Infinity) {
            this.buttonRestoreTimeoutId = setTimeout(() => {
                this.setUndoDisabled(Store.moves.length == 0);
                this.setHintDisabled(false);
                this.setWandDisabled(false);
                this.setStoriesDisabled(false);
                this.buttonRestoreTimeoutId = undefined;
            }, duration);
        }
    }

    public removeDifficulty(): void {
        this.updateUI({
            gameStats: {
                ...uiStore.getState().gameUI.gameStats,
                showDifficulty: false
            }
        });
    }

    private OnUndoClicked() {
        SoundManager.play('undoSFX');
        GameEvent.emit(EventName.UNDO_TRIGGERED);
        Store.setUndoCounter(Store.undoCounter - 1);
        this.setUndoDisabled(Store.moves.length == 0);
    }

    private UpdateWandCounter() {
        this.updateUI({
            buttons: {
                ...uiStore.getState().gameUI.buttons,
                magicWandsCount: Store.wandCounter
            }
        });
    }

    private updateHintsCounter() {
        this.updateUI({
            indicators: {
                ...uiStore.getState().gameUI.indicators,
                hintsCount: Store.hintCounter
            }
        });
    }

    private onHintClicked() {
        if (Store.hintCounter < 1) {
            GameEvent.emit(EventName.SHOW_ADS, { place: 'hint' });
            return;
        }
        GameEvent.emit(EventName.HINT);

        GameEvent.emit(EventName.UPDATE_HINT_BUTTON);
    }

    rulesClick() {
        if (!Store.availableGamesToTutorial.includes(Store.name)) {
            GAEvent(GAEventNames.GAME_RULES_OPEN, {
                timeSec: Store.time,
            });
            let url = '';
            switch (Store.name) {
                case GameName.KLONDIKE:
                    url = translate('rulesKlondikeURL');
                    break;
                case GameName.SPIDER:
                    url = translate('rulesSpiderURL');
                    break;
                case GameName.FREECELL:
                    url = translate('rulesFreecellURL');
                    break;
                case GameName.PYRAMID:
                    url = translate('rulesPyramidURL');
                break;
            }
    
            if (isIOS()) window.location.href = url;
            else window.open(url, '_blank');
            
            return;
        }

        GameUI.showPopup(Popups.TUTORIAL_START);

        GAEvent(GAEventNames.TUTORIAL_SHOW_BANNER, {
            number: Store.countPopupShown('tutorial_start'),
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
        });

        if (import.meta.url.includes('localhost:5173')) {
            dispatchEvent(new CustomEvent('closeModal', { detail: { id: 'tutorial_start', confirm: true } }));
        }
    }

    public Init() {
        this.updateUI({
            ...uiStore.getState().gameUI,
            gameStats: {
                ...uiStore.getState().gameUI.gameStats,
                difficulty: translate(Store.getDifficultyText()),
                moves: Store.movesCount,
                time: Store.getTimeFormat(Store.time),
                showDifficulty: !Store.isPyramid,
            },
            labels: {
                movesLabel: translate('moves'),
                timeLabel: translate('time'),
                settingsText: translate('settings'),
                feedbackText: translate('leaveFeedback'),
                designText: translate('design'),
                autocompleteText: translate('autocomplete')
            },
            indicators: {
                hintsCount: Store.hintCounter,
                hintsIndicator: false,
                settingsIndicator: false,
                shareIndicator: false
            },
            settings: this.options.map(option => ({
                checked: option.valueGetter(Store),
                visible: option.visible.includes(Store.name),
                key: option.key,
                label: translate(option.key)
            })),
            design: {
                selectedPattern: Store.back,
                selectedCardTheme: Store.front,
                selectedBackground: Store.background
            },
            buttons: {
                undoDisabled: Store.moves.length == 0,
                showMagicWand: !Store.isPyramid,
                hideDesignFirstTab: Store.isFreecell,
                showAutocomplete: false,
                magicWandDisabled: false,
                storiesDisabled: false,
                hintsDisabled: false,
                magicWandsCount: Store.wandCounter
            },
            modals: {
                ...uiStore.getState().gameUI.modals,
                designModal: {
                    isOpen: false,
                    activeTab: 'patterns'
                },
                settingsModal: {
                    isOpen: false
                }
            },
            isExpanded: false,
            tutorialActive: Store.tutorialActive,
            isInitialized: true
        });
    }

    public updateGameStats(stats: { moves?: number; time?: string; difficulty?: string }) {
        this.updateUI({
            gameStats: {
                ...uiStore.getState().gameUI.gameStats,
                ...stats
            }
        });
    }

    public updateTime(timeString: string) {
        this.updateUI({
            gameStats: {
                ...uiStore.getState().gameUI.gameStats,
                time: timeString
            }
        });
    }

    public updateMoves(movesCount: number) {
        this.updateUI({
            gameStats: {
                ...uiStore.getState().gameUI.gameStats,
                moves: movesCount
            }
        });
    }

    public setHintsCount(count: number) {
        this.updateUI({
            indicators: {
                ...uiStore.getState().gameUI.indicators,
                hintsCount: count
            }
        });
    }

    public setIndicator(key: 'hintsIndicator' | 'settingsIndicator' | 'shareIndicator', value: boolean) {
        this.updateUI({
            indicators: {
                ...uiStore.getState().gameUI.indicators,
                [key]: value
            }
        });
    }

    public static showPopup(popup: Popups, data?: PopupStateData) {
        uiStore.dispatch(openPopup(popup, data));
        document.dispatchEvent(
            new CustomEvent("openedModal", {
                detail: { id: popup },
            })
        );
        Store.addNewShowedPopup(popup);
    }

    public static closePopup(popup: Popups, confirm: boolean = false) {
        uiStore.dispatch(closePopup(popup));
        dispatchEvent(new CustomEvent('closeModal', { detail: { id: popup, confirm } }));
    }
}